import React from 'react';
import NextImage, { ImageProps } from 'next/image';
import qsStringify from '@sls-react/utils/qsStringify';
import { useRouter } from '@sls-react/routing';

interface FastlyImageOptimizerOptions {
  auto?: 'webp';
  'bg-color'?: string;
  blur?: string;
  brightness?: number | string;
  canvas?: string;
  contrast?: number | string;
  crop?: number | string;
  disable?: 'upscale';
  dpr?: number;
  enable?: 'upscale';
  fit?: 'bounds' | 'cover' | 'crop';
  format?:
    | 'gif'
    | 'png'
    | 'png8'
    | 'jpg'
    | 'jpeg'
    | 'bjpg'
    | 'pjpg'
    | 'webp'
    | 'webpll'
    | 'webply'
    | 'mp4';
  frame?: 1;
  height?: string | number;
  level?: string;
  optimize?: 'low' | 'medium' | 'high';
  orient?: 'r' | 'l' | 'h' | 'v' | 'hv' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  pad?: string;
  precrop?: FastlyImageOptimizerOptions['crop'];
  profile?: 'baseline' | 'main' | 'high';
  quality?: number | string;
  'resize-filter'?:
    | 'nearest'
    | 'bilinear'
    | 'linear'
    | 'cubic'
    | 'bicubic'
    | 'lanczos2'
    | 'lanczos'
    | 'lanczos3';
  saturation?: string | number;
  sharpen?: string;
  trim?: string; // top, right, bottom, left
  width?: number | string;
}

export function generateFastlyImageUrl(
  src: string,
  options?: FastlyImageOptimizerOptions | true,
  enabled = true,
) {
  if (enabled && imagesUrlRe.test(src)) {
    // Replace the first 31 characters with the CDN
    return `https://edge.images.sidelineswap.com${src.slice(31)}${qsStringify(
      typeof options === 'object' ? options : null,
    )}`;
  }

  return src;
}

const imagesUrlRe = new RegExp('^https://images\\.sidelineswap\\.com');

interface StaticRequire {
  default: any;
}
declare type StaticImport = StaticRequire | any;

interface Props
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'placeholder'
  > {
  useFastly?: boolean | FastlyImageOptimizerOptions;
  useFastlyBlurPlaceholder?: boolean | FastlyImageOptimizerOptions;
  useNextJSImage?: true | Omit<ImageProps, 'src'>;
  src: string | StaticImport;
}

const Image = React.forwardRef<HTMLImageElement, Props>(
  (
    {
      className,
      src,
      useFastly,
      useFastlyBlurPlaceholder,
      useNextJSImage,
      ...props
    },
    ref,
  ) => {
    const router = useRouter();
    const fastlyIsDisabled = router.query.disable_fastlyio !== '1';

    const imageUrl =
      typeof src === 'string' ? src : src?.src || src?.default?.src;

    if (!imageUrl) {
      return (
        <img loading="lazy" src={imageUrl} className={className} {...props} />
      );
    }

    if (useNextJSImage && typeof imageUrl === 'string') {
      return (
        <NextImage
          layout="fill"
          loader={resolverProps => {
            if (useFastly) {
              let options: FastlyImageOptimizerOptions = {
                width: resolverProps.width,
              };

              if (typeof useFastly === 'object') {
                options = {
                  ...options,
                  ...useFastly,
                };
              }

              return generateFastlyImageUrl(
                resolverProps.src,
                options,
                fastlyIsDisabled,
              );
            }

            return imageUrl;
          }}
          loading="lazy"
          placeholder={useFastlyBlurPlaceholder ? 'blur' : undefined}
          blurDataURL={
            useFastlyBlurPlaceholder
              ? generateFastlyImageUrl(
                  imageUrl,
                  {
                    height: 16,
                    width: 16,
                    ...(typeof useFastlyBlurPlaceholder === 'object'
                      ? useFastlyBlurPlaceholder
                      : {}),
                  },
                  fastlyIsDisabled,
                )
              : undefined
          }
          src={imageUrl}
          className={className}
          {...props}
          {...useNextJSImage}
        />
      );
    }

    return (
      <img
        loading="lazy"
        src={
          useFastly
            ? generateFastlyImageUrl(imageUrl, useFastly, fastlyIsDisabled)
            : imageUrl
        }
        className={className}
        ref={ref}
        {...props}
      />
    );
  },
);

export default Image;
